import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Snackbar, Alert, Chip } from '@mui/material';
import ChatSidebar from '../components/ChatSidebar';
import ChatArea from '../components/ChatArea';
import { getConversations, getMessages, sendMessage, sendMedia, exportChats, markAsRead, blockNumber, unblockNumber } from '../api/chat';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { initSound, playNotificationSound } from '../utils/notificationSound';

const POLL_INTERVAL = 30000;

const ClientChat = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const pollRef = useRef(null);
  const seenRef = useRef({});
  const lastAlertRef = useRef({});

  const safeTs = (m) => {
    const t = new Date(m && (m.timestamp || m.createdAt || m.updatedAt));
    return isNaN(t.getTime()) ? null : t;
  };

  const checkNewMessages = useCallback((convs) => {
    for (const c of convs) {
      if (c.blocked) continue;
      const msgs = c.messages || [];
      const last = msgs[msgs.length - 1];
      if (!last || last.sender !== "user") continue;
      const ts = safeTs(last);
      const sig = `${ts ? ts.toISOString() : "na|"}|${last.text}`;
      if (seenRef.current[c.sessionId] === sig) continue;
      seenRef.current[c.sessionId] = sig;
      if (lastAlertRef.current[c.sessionId] && Date.now() - lastAlertRef.current[c.sessionId] < 5000) continue;
      playNotificationSound();
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(c.userName || c.sessionId || 'WhatsApp', { body: last.text || 'New message', icon: '/talktokam-green-wa.png' });
      }
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const data = await getConversations();
      const convs = data.conversations || [];
      setConversations(convs);
      checkNewMessages(convs);
      return convs;
    } catch (err) {
      console.error('Failed to fetch conversations', err);
      return [];
    }
  }, [checkNewMessages]);

  const fetchMessages = useCallback(async (sessionId) => {
    try {
      const data = await getMessages(sessionId);
      setMessages(data.messages || []);
      setSelectedConvo(data.conversation || null);
    } catch (err) {
      console.error('Failed to fetch messages', err);
    }
  }, []);

  const requestNotif = useCallback(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Initial load
  useEffect(() => {
    initSound();
    requestNotif();
    fetchConversations().then(() => setLoading(false));
  }, [fetchConversations, requestNotif]);

  // Polling fallback — refreshes conversations every 30s
  useEffect(() => {
    const tick = () => {
      fetchConversations();
      if (selectedSession) fetchMessages(selectedSession);
    };
    pollRef.current = setInterval(tick, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, selectedSession]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchConversations();
        if (selectedSession) fetchMessages(selectedSession);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchConversations, fetchMessages, selectedSession]);

  const handleSelect = async (sessionId) => {
    if (selectedSession === sessionId) {
      setMessages([]);
      try { await markAsRead(sessionId); } catch {}
      fetchMessages(sessionId);
      fetchConversations();
      return;
    }
    setSelectedSession(sessionId);
    setMessages([]);
    try { await markAsRead(sessionId); } catch {}
    fetchMessages(sessionId);
    fetchConversations();
  };

  // Socket auto-refresh
  const handleSocketMessage = useCallback(async (data) => {
    const convs = await fetchConversations();
    if (data.from && data.from === selectedSession) fetchMessages(data.from);

    if (data.isAutoReply) return;

    if (convs.find(c => c.sessionId === data.from)?.blocked) return;

    const sig = `${safeTs(data) ? safeTs(data).toISOString() : "na|"}|${data.text}`;
    if (seenRef.current[data.from] === sig) return;
    seenRef.current[data.from] = sig;
    playNotificationSound();
    if ('Notification' in window && Notification.permission === 'granted') {
      const convo = convs.find(c => c.sessionId === data.from);
      const name = convo?.userName || data.from || 'WhatsApp';
      const text = data.text || 'New message';
      new Notification(name, { body: text, icon: '/talktokam-green-wa.png' });
    }
    lastAlertRef.current[data.from] = Date.now();
  }, [fetchConversations, fetchMessages, selectedSession]);

  const { connected } = useSocket(user?._id, handleSocketMessage);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchConversations();
    if (selectedSession) await fetchMessages(selectedSession);
    setRefreshing(false);
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const blob = await exportChats();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'all-chats.csv'; a.click();
      window.URL.revokeObjectURL(url);
      setSnack({ open: true, message: 'All chats exported', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Export failed', severity: 'error' });
    }
    setExporting(false);
  };

  const handleExportSingle = async (sessionId) => {
    setExporting(true);
    try {
      const blob = await exportChats(sessionId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `chat-${sessionId}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      setSnack({ open: true, message: 'Chat exported', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Export failed', severity: 'error' });
    }
    setExporting(false);
  };

  const handleBlock = async (sessionId) => {
    setBlocking(true);
    try {
      await blockNumber(sessionId);
      setSnack({ open: true, message: `Number ${sessionId} blocked`, severity: 'success' });
      fetchMessages(sessionId);
      fetchConversations();
    } catch {
      setSnack({ open: true, message: 'Failed to block number', severity: 'error' });
    }
    setBlocking(false);
  };

  const handleUnblock = async (sessionId) => {
    setBlocking(true);
    try {
      await unblockNumber(sessionId);
      setSnack({ open: true, message: `Number ${sessionId} unblocked`, severity: 'success' });
      fetchMessages(sessionId);
      fetchConversations();
    } catch {
      setSnack({ open: true, message: 'Failed to unblock number', severity: 'error' });
    }
    setBlocking(false);
  };

  const handleSend = async (text) => {
    if (!selectedSession) return;
    try {
      await sendMessage(selectedSession, text);
      fetchMessages(selectedSession);
      fetchConversations();
    } catch (err) {
      setSnack({ open: true, message: 'Failed to send', severity: 'error' });
    }
  };

  const handleSendMedia = async (file, caption) => {
    if (!selectedSession) return;
    try {
      await sendMedia(selectedSession, file, caption);
      fetchMessages(selectedSession);
      fetchConversations();
      setSnack({ open: true, message: 'Media sent', severity: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.message || err.message || '';
      setSnack({ open: true, message: detail.includes('Media upload') || detail.includes('Send failed') ? detail.slice(0, 150) : 'Failed to send media', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100%', bgcolor: '#f0f2f5' }}>
        <ChatSidebar conversations={[]} loading={true} selectedSession={null} onSelect={() => {}} />
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Loading chats...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 20px)', bgcolor: '#f0f2f5' }}>
      {!connected && (
        <Box sx={{ px: 2, py: 0.5, bgcolor: '#fff3cd', borderBottom: '1px solid #ffc107', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label="Reconnecting…" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#f59e0b', color: '#fff', fontWeight: 600 }} />
          <Typography variant="caption" color="#92400e">Live updates paused. Auto-refreshing every 30s.</Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ChatSidebar
          conversations={conversations}
          selectedSession={selectedSession}
          onSelect={(id) => { requestNotif(); handleSelect(id); }}
          loading={false}
          onRefresh={handleRefresh}
          onExportAll={handleExportAll}
          refreshing={refreshing}
          exporting={exporting}
        />
        <ChatArea
          messages={messages}
          sessionId={selectedSession}
          conversation={selectedConvo}
          onSend={handleSend}
          onSendMedia={handleSendMedia}
          currentUser={user?._id}
          onExport={handleExportSingle}
          blocked={selectedConvo?.blocked}
          unsubscribed={selectedConvo?.subscriptionStatus === 'unsubscribed'}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          blocking={blocking}
        />
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}
          sx={{ width: '100%', borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientChat;
